import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { WaveSawtooth, Sparkle, Check, X, PencilSimple } from "@phosphor-icons/react";
import PageHeader from "@/components/PageHeader";

/** Tiny inline word-level diff renderer. */
function DiffView({ original, edited }) {
  const parts = useMemo(() => wordDiff(original || "", edited || ""), [original, edited]);
  return (
    <div className="grid md:grid-cols-2 gap-3">
      <div className="p-3 rounded-sm border border-[color:var(--oz-border)] bg-[color:var(--oz-page)]">
        <div className="overline mb-2 text-[color:var(--oz-text-muted)]">AI drafted</div>
        <div className="text-sm whitespace-pre-wrap leading-relaxed">
          {parts.map((p, i) =>
            p.op === "delete" ? (
              <span key={i} className="bg-red-500/15 text-red-300 line-through px-0.5 rounded-sm">{p.text}</span>
            ) : p.op === "keep" ? (
              <span key={i}>{p.text}</span>
            ) : null,
          )}
        </div>
      </div>
      <div className="p-3 rounded-sm border border-[color:var(--oz-emerald)]/40 bg-[color:var(--oz-emerald)]/5">
        <div className="overline mb-2" style={{ color: "var(--oz-emerald)" }}>Mark rewrote</div>
        <div className="text-sm whitespace-pre-wrap leading-relaxed">
          {parts.map((p, i) =>
            p.op === "insert" ? (
              <span key={i} className="bg-emerald-500/20 text-emerald-200 px-0.5 rounded-sm">{p.text}</span>
            ) : p.op === "keep" ? (
              <span key={i}>{p.text}</span>
            ) : null,
          )}
        </div>
      </div>
    </div>
  );
}

/** Word-level LCS diff → sequence of {op: keep|insert|delete, text} */
function wordDiff(a, b) {
  const A = a.split(/(\s+)/);
  const B = b.split(/(\s+)/);
  const m = A.length, n = B.length;
  const dp = Array.from({ length: m + 1 }, () => new Int32Array(n + 1));
  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      dp[i][j] = A[i] === B[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const out = [];
  let i = 0, j = 0;
  while (i < m && j < n) {
    if (A[i] === B[j]) { out.push({ op: "keep", text: A[i] }); i++; j++; }
    else if (dp[i + 1][j] >= dp[i][j + 1]) { out.push({ op: "delete", text: A[i] }); i++; }
    else { out.push({ op: "insert", text: B[j] }); j++; }
  }
  while (i < m) { out.push({ op: "delete", text: A[i++] }); }
  while (j < n) { out.push({ op: "insert", text: B[j++] }); }
  return out;
}

export default function Voice() {
  const [stats, setStats] = useState(null);
  const [edits, setEdits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [learning, setLearning] = useState(false);
  const [preview, setPreview] = useState(null); // {previous_profile, new_profile}
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileDraft, setProfileDraft] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [s, e] = await Promise.all([api.get("/voice/stats"), api.get("/voice/edits")]);
      setStats(s.data);
      setEdits(e.data);
      setProfileDraft(s.data.current_voice_profile || "");
    } catch { toast.error("Failed to load voice data"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const preview_learn = async () => {
    setLearning(true);
    try {
      const { data } = await api.post("/voice/learn", { dry_run: true });
      setPreview(data);
      toast.success(`Analyzed ${data.edits_analyzed} edits`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Learn failed");
    } finally { setLearning(false); }
  };

  const commit_learn = async () => {
    setLearning(true);
    try {
      const { data } = await api.post("/voice/learn", { dry_run: false });
      toast.success("New voice profile saved. Next drafts will use it.");
      setPreview(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setLearning(false); }
  };

  const saveProfile = async () => {
    try {
      await api.put("/voice/profile", { voice_profile: profileDraft });
      toast.success("Profile saved");
      setEditingProfile(false);
      await load();
    } catch { toast.error("Save failed"); }
  };

  if (loading) return <div className="p-8 text-[color:var(--oz-text-dim)]">Loading Voice Lab…</div>;

  const readyPct = Math.min(100, Math.round((stats.total_edits / stats.recommended_min_for_quality) * 100));

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <PageHeader
        overline="Learn my voice"
        title="Voice Lab"
        subtitle="Every time you edit an AI draft, we capture the diff. Feed the diffs back to Claude and it rewrites your voice profile — sharper drafts every cycle."
      />

      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="oz-card p-4">
          <div className="overline mb-1">Edits captured</div>
          <div className="font-mono text-2xl">{stats.total_edits}</div>
          <div className="text-[10px] text-[color:var(--oz-text-muted)] mt-1 font-mono">
            recommended: {stats.recommended_min_for_quality}
          </div>
          <div className="prio-bar mt-2">
            <div className="prio-fill" style={{ width: `${readyPct}%`, background: readyPct >= 100 ? "var(--oz-emerald)" : "var(--oz-gold)" }} />
          </div>
        </div>
        <div className="oz-card p-4">
          <div className="overline mb-1">Avg AI draft</div>
          <div className="font-mono text-2xl">{stats.avg_original_length}</div>
          <div className="text-[10px] text-[color:var(--oz-text-muted)] mt-1 font-mono">chars</div>
        </div>
        <div className="oz-card p-4">
          <div className="overline mb-1">Avg Mark edit</div>
          <div className="font-mono text-2xl text-emerald-400">{stats.avg_edited_length}</div>
          <div className="text-[10px] text-[color:var(--oz-text-muted)] mt-1 font-mono">chars</div>
        </div>
        <div className="oz-card p-4">
          <div className="overline mb-1">Δ length</div>
          <div className="font-mono text-2xl text-[color:var(--oz-gold)]">{stats.avg_length_delta}</div>
          <div className="text-[10px] text-[color:var(--oz-text-muted)] mt-1 font-mono">shorter/longer</div>
        </div>
      </div>

      {/* Current profile */}
      <div className="oz-card p-5 mb-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <WaveSawtooth size={16} color="var(--oz-gold)" />
            <div className="overline">Current voice profile</div>
          </div>
          {!editingProfile && (
            <button onClick={() => setEditingProfile(true)} className="btn-ghost text-xs flex items-center gap-1" data-testid="edit-profile-btn">
              <PencilSimple size={12} /> Edit manually
            </button>
          )}
        </div>
        {editingProfile ? (
          <div>
            <textarea
              value={profileDraft}
              onChange={(e) => setProfileDraft(e.target.value)}
              rows={6}
              className="w-full bg-[color:var(--oz-page)] border border-[color:var(--oz-border)] rounded-sm p-3 text-sm leading-relaxed"
              data-testid="profile-textarea"
            />
            <div className="flex gap-2 mt-2">
              <button onClick={saveProfile} className="btn-primary text-xs" data-testid="save-profile-btn">Save</button>
              <button onClick={() => { setEditingProfile(false); setProfileDraft(stats.current_voice_profile); }} className="btn-ghost text-xs">Cancel</button>
            </div>
          </div>
        ) : (
          <p className="text-sm leading-relaxed text-[color:var(--oz-text)]" data-testid="current-profile">
            {stats.current_voice_profile || <span className="text-[color:var(--oz-text-muted)] italic">No profile yet — the AI is drafting generically.</span>}
          </p>
        )}
      </div>

      {/* Learn button + preview */}
      <div className="oz-card p-5 mb-6">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div className="flex-1">
            <div className="font-display text-lg mb-1">Learn my voice</div>
            <div className="text-xs text-[color:var(--oz-text-dim)]">
              Send all {stats.total_edits} captured edits to Claude Sonnet 4.5. It studies the patterns and returns a sharpened voice profile.
              {stats.total_edits < 20 && (
                <span className="text-[color:var(--oz-amber)]"> Below 20 edits, results are directional — the more edits, the sharper the model gets.</span>
              )}
            </div>
          </div>
          <button
            onClick={preview_learn}
            disabled={learning || !stats.ready_to_learn}
            className="btn-primary text-sm flex items-center gap-1 shrink-0"
            data-testid="learn-btn"
          >
            <Sparkle size={12} weight="fill" /> {learning ? "Analyzing…" : "Preview new voice"}
          </button>
        </div>
        {!stats.ready_to_learn && (
          <div className="text-xs text-[color:var(--oz-amber)] font-mono">Need at least 3 edits to run analysis.</div>
        )}

        {preview && (
          <div className="mt-5 pt-5 border-t border-[color:var(--oz-border)] animate-fade-in" data-testid="preview-panel">
            <div className="overline mb-2" style={{ color: "var(--oz-emerald)" }}>Proposed new profile</div>
            <p className="text-sm leading-relaxed p-3 border border-[color:var(--oz-emerald)]/40 bg-[color:var(--oz-emerald)]/5 rounded-sm mb-3">
              {preview.new_profile}
            </p>
            <div className="flex gap-2">
              <button onClick={commit_learn} disabled={learning} className="btn-primary text-sm flex items-center gap-1" data-testid="commit-learn-btn">
                <Check size={12} /> Adopt this profile
              </button>
              <button onClick={() => setPreview(null)} className="btn-ghost text-sm flex items-center gap-1">
                <X size={12} /> Discard
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Diff timeline */}
      <div className="mb-3">
        <div className="overline mb-3">Recent edits ({edits.length})</div>
        {edits.length === 0 && (
          <div className="oz-card p-8 text-center text-sm text-[color:var(--oz-text-muted)]">
            No edits captured yet. Edit any AI-drafted reply on The Curtain — the diff will show up here.
          </div>
        )}
        <div className="space-y-4">
          {edits.map((e) => (
            <div key={e.id || e.message_id} className="oz-card p-4" data-testid={`edit-${e.message_id}`}>
              {e.client_msg && (
                <div className="text-[11px] text-[color:var(--oz-text-muted)] font-mono mb-3 truncate">
                  ↳ Reply to: &quot;{e.client_msg.slice(0, 140)}&quot;
                </div>
              )}
              <DiffView original={e.original} edited={e.edited} />
              <div className="text-[10px] text-[color:var(--oz-text-muted)] font-mono mt-2">
                {new Date(e.created_at).toLocaleString()}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
